import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiClientV2 } from '~/api/client';
import clientApi, {
  IClientApiResponse,
  mapCreatorsFromApiModelToViewModel,
} from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import {
  SAVED_LIST_CREATORS,
  SAVED_LIST_CREATORS_SEARCH,
} from '~/queries/keys';
import { ICreator } from '~/types';

const SHARED_SAVED_LIST_CREATORS_PAGE_SIZE = 10;

interface Params {
  creatorIds?: string[];
  id: string;
  pageSize?: number;
  properties?: string[];
  sortDirection?: string;
  sortField?: string;
}

export const useGetSavedListCreators = ({
  creatorIds,
  id,
  pageSize = SHARED_SAVED_LIST_CREATORS_PAGE_SIZE,
  properties,
  sortField,
  sortDirection,
}: Params) => {
  const queryKey: any[] = [
    SAVED_LIST_CREATORS,
    id,
    { creatorIds, pageSize, sortField, sortDirection, properties },
  ];

  const query = useInfiniteQuery<
    IClientApiResponse<Creator>,
    Error,
    { creators: ICreator[]; totalItems: number },
    any
  >({
    queryKey,
    queryFn: ({ pageParam = 1 }) =>
      clientApi.savedLists.creators({
        'sort[]': `${sortField}:${sortDirection}`,
        creatorIds,
        id,
        page: pageParam,
        pageSize,
        properties,
      }),
    enabled: !!id,
    staleTime: 1000 * 60 * 30, //30 mins
    select: (data) => {
      const pages = data.pages.reduce(
        (acc, page) => {
          acc.results = [...acc.results, ...page.results];
          acc.totalItems = page.totalItems;

          return acc;
        },
        {
          results: [],
          totalItems: 0,
        },
      );

      return {
        creators: mapCreatorsFromApiModelToViewModel(pages.results),
        totalItems: pages.totalItems,
      };
    },
    getNextPageParam: (page, pages) =>
      Math.floor(pages.length * pageSize) <= (page?.totalItems ?? 0)
        ? pages.length + 1
        : undefined,
  });

  return { ...query, queryKey };
};

export const useGetSavedListCreatorsAll = ({ id }: { id: string }) => {
  const [isLoading, setIsLoading] = useState(true);

  const creators = useGetSavedListCreators({ id });

  useEffect(() => {
    // Iteratively load next page(s)
    if (creators.hasNextPage === false || !id) {
      setIsLoading(false);
      return;
    }

    // Has next page
    creators.fetchNextPage();
  }, [creators, id]);

  // @ts-ignore - types f'ed up
  return { isLoading, creators: creators.data?.creators || [] };
};

interface UseSavedListCreatorsV2Params {
  creatorIds?: string[];
  enabled?: boolean;
  id: string;
}

type SavedListCreator = {
  creatorId: string;
  notes: string;
};

export const useGetSavedListCreatorsSearch = ({
  creatorIds,
  enabled = true,
  id,
}: UseSavedListCreatorsV2Params) => {
  const queryKey = [SAVED_LIST_CREATORS_SEARCH, id, { creatorIds }];

  const { data, ...query } = useQuery({
    queryKey,
    queryFn: () =>
      apiClientV2.post(
        `/saved-lists/${id}/creators/notes/search`,
        {
          creatorIds,
        },
        {
          headers: {
            'content-type': 'application/json',
          },
        },
      ),
    select: ({ data }) => {
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
    enabled: !!id && enabled && !!creatorIds?.length,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  return {
    ...query,
    data: data as SavedListCreator[],
    queryKey,
  };
};
